import React, { useState, useRef } from 'react';
import { Button, Icon, Loader } from '@pwc/pwc-deals-ui';
import { Overlay, Popover } from 'react-bootstrap';
import { cond, constant, stubTrue } from 'lodash';
import { deleteLibraryItem, isDeletable } from '../libraryItemService';
import './LibraryItemDeleteButton.scss';

const DeleteButton = ({ libraryItemId, disabled, ...props }) => (
  <Button
    className="delete-library-item-button button-small"
    data-testid={`delete-library-item-button-${libraryItemId}`}
    disabled={disabled}
    {...props}
  >
    <Icon
      name="delete"
      className={disabled ? 'fill icon-grey-medium' : 'fill icon-orange'}
      width="24"
      height="24"
    />
  </Button>
);

const POPOVER_STATES = {
  NOT_DELETABLE: 1,
  CONFIRMATION: 2,
  ERROR: 3,
};

const useLibraryItemDeletion = (projectId, libraryItemId, onDelete) => {
  const [show, setShow] = useState(false);
  const [target, setTarget] = useState(null);
  const [popoverState, setPopoverState] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const ref = useRef(null);

  const fetchIsDeletable = async () => {
    if (!popoverState) {
      setIsLoading(true);
      try {
        const deletable = await isDeletable(projectId, libraryItemId);
        setIsLoading(false);
        if (deletable) {
          return setPopoverState(POPOVER_STATES.CONFIRMATION);
        }
        return setPopoverState(POPOVER_STATES.NOT_DELETABLE);
      } catch (err) {
        setIsLoading(false);
        return setPopoverState(POPOVER_STATES.ERROR);
      }
    }
    return null;
  };

  const handleClose = (event) => {
    event.preventDefault();
    setShow(false);
    setPopoverState();
  };

  const handleToggle = async (event) => {
    event.preventDefault();
    if (!show) {
      fetchIsDeletable();
      setShow(true);
    } else {
      setShow(false);
    }
    setTarget(event.target);
  };

  const handleDelete = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await deleteLibraryItem(projectId, libraryItemId);
      setIsLoading(false);
      setShow(false);
      return onDelete && onDelete();
    } catch (err) {
      setIsLoading(false);
      return setPopoverState(POPOVER_STATES.ERROR);
    }
  };

  const getPopoverData = cond([
    [
      () => popoverState === POPOVER_STATES.NOT_DELETABLE,
      constant({
        title: 'Item in use',
        description: 'This library item cannot be deleted at this time.',
        close: handleClose,
      }),
    ],
    [
      () => popoverState === POPOVER_STATES.CONFIRMATION,
      constant({
        title: 'Delete this item?',
        description: 'This will remove the file from the platform.',
        close: handleClose,
        delete: handleDelete,
      }),
    ],
    [
      () => popoverState === POPOVER_STATES.ERROR,
      constant({
        title: 'An error occured',
        description: 'This library item could not be deleted at this time.',
        close: handleClose,
      }),
    ],
    [stubTrue, constant(null)],
  ]);

  return {
    ref,
    target,
    show,
    handleClose,
    handleToggle,
    popoverData: getPopoverData(),
    isLoading,
  };
};

export default ({ projectId, libraryItemId, onDelete, disabled }) => {
  const {
    ref,
    target,
    show,
    handleClose,
    handleToggle,
    popoverData,
    isLoading,
  } = useLibraryItemDeletion(projectId, libraryItemId, onDelete);

  return (
    <div ref={ref}>
      <DeleteButton
        disabled={disabled}
        libraryItemId={libraryItemId}
        onClick={handleToggle}
      />
      <Overlay
        show={show}
        target={target}
        placement="left"
        rootClose
        onHide={handleClose}
        container={ref.current}
      >
        <Popover
          id={`popover-delete-library-item-${libraryItemId}`}
          data-testid={`popover-delete-library-item-${libraryItemId}`}
          className="popover-delete-library-item"
        >
          {popoverData && (
            <>
              <Popover.Content>
                {popoverData.title && (
                  <Popover.Title data-testid={`popover-title-${libraryItemId}`}>
                    {popoverData.title}
                  </Popover.Title>
                )}
                {popoverData.title && (
                  <span data-testid={`popover-description-${libraryItemId}`}>
                    {popoverData.description}
                  </span>
                )}
                {(popoverData.close || popoverData.delete) && (
                  <div className="actions-container">
                    {popoverData.close && (
                      <Button
                        data-testid={`popover-close-${libraryItemId}`}
                        onClick={popoverData.close}
                        className="button-outline button-xs close"
                      >
                        Close
                      </Button>
                    )}
                    {popoverData.delete && (
                      <Button
                        data-testid={`popover-delete-confirmation-${libraryItemId}`}
                        onClick={popoverData.delete}
                        disabled={isLoading}
                        className="button-xs confirm"
                      >
                        Yes, delete
                      </Button>
                    )}
                  </div>
                )}
              </Popover.Content>
            </>
          )}
          {!popoverData && isLoading && (
            <Popover.Content>
              <Loader dataTestId={`popover-loader-${libraryItemId}`} />
            </Popover.Content>
          )}
        </Popover>
      </Overlay>
    </div>
  );
};
